/**
 * StockOverviewDashboard Component
 *
 * Main dashboard component for inventory stock overview.
 * Displays:
 * - Category summary cards (Raw Materials, Packaging, Sundry)
 * - Low stock items with progress bars
 * - Recent stock movement activity
 *
 * User Story: US-P6-001 - Inventory Management
 * Step 4: Stock Overview Dashboard
 */

import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertTriangle,
  Package,
  ShoppingCart,
  RefreshCw
} from 'lucide-react'
import { cn } from '@/lib/utils'

import { useInventoryItems, useLowStockItems } from '@/hooks/useInventoryItems'
import { useInventoryBatches } from '@/hooks/useInventoryBatches'

import StockLevelBar from './StockLevelBar'
import StockMovementHistory from './StockMovementHistory'

/**
 * Loading skeleton for the dashboard
 */
function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      {/* Low stock skeleton */}
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-2 w-full" />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Recent activity skeleton */}
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Low stock item row with progress bar and action button
 */
function LowStockItem({ item, onOrder }) {
  return (
    <div className="flex items-center gap-4 py-3 border-b border-border last:border-b-0">
      {/* Item info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-foreground truncate">{item.name}</p>
          <Badge variant="outline" className="text-xs flex-shrink-0">
            {item.category?.replace('_', ' ')}
          </Badge>
        </div>

        {/* Stock level bar */}
        <div className="mt-2">
          <StockLevelBar
            currentStock={item.current_stock || 0}
            reorderPoint={item.reorder_point || 0}
            unit={item.unit_of_measure}
            showLabel={true}
          />
        </div>
      </div>

      {/* Order action */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onOrder(item)}
        className="flex-shrink-0"
      >
        <ShoppingCart className="h-4 w-4 mr-1" />
        Order
      </Button>
    </div>
  )
}

/**
 * Empty state for low stock section
 */
function NoLowStockItems() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <Package className="h-10 w-10 text-emerald-500 mb-3" />
      <p className="text-sm font-medium text-foreground">All stock levels healthy</p>
      <p className="text-xs text-muted-foreground mt-1">
        No items are below their reorder point
      </p>
    </div>
  )
}

/**
 * StockOverviewDashboard Component
 *
 * @param {Object} props
 * @param {string} props.className - Additional CSS classes
 */
export default function StockOverviewDashboard({ className = '' }) {
  const navigate = useNavigate()

  // Fetch data from existing hooks
  const {
    isLoading: itemsLoading,
    error: itemsError,
    refetch: refetchItems
  } = useInventoryItems({ activeOnly: true })

  const {
    lowStockItems,
    isLoading: lowStockLoading,
    refetch: refetchLowStock
  } = useLowStockItems()

  const {
    batches,
    isLoading: batchesLoading,
    refetch: refetchBatches
  } = useInventoryBatches()

  const isLoading = itemsLoading || lowStockLoading || batchesLoading

  const handleRefresh = () => {
    refetchItems()
    refetchLowStock()
    refetchBatches()
  }

  const handleAddPurchase = () => {
    navigate('/inventory/purchases')
  }

  const handleOrderItem = (item) => {
    // Navigate to purchases page with item pre-selected
    // For now, just navigate to purchases
    navigate('/inventory/purchases')
  }

  if (isLoading) {
    return <DashboardSkeleton />
  }

  if (itemsError) {
    return (
      <Card className={cn('', className)}>
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center text-center">
            <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              Failed to load inventory
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {itemsError}
            </p>
            <Button variant="outline" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Try again
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Low Stock Items Section */}
      <Card className={lowStockItems.length > 0 ? 'border-amber-200' : ''}>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            {lowStockItems.length > 0 && (
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            )}
            <CardTitle className="text-base">
              Items Below Reorder Point
            </CardTitle>
            {lowStockItems.length > 0 && (
              <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-200">
                {lowStockItems.length}
              </Badge>
            )}
          </div>
          {lowStockItems.length > 0 && (
            <CardDescription>
              These items need to be reordered soon
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {lowStockItems.length === 0 ? (
            <NoLowStockItems />
          ) : (
            <div>
              {lowStockItems.map((item) => (
                <LowStockItem
                  key={item.id}
                  item={item}
                  onOrder={handleOrderItem}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <StockMovementHistory batches={batches} limit={10} />
    </div>
  )
}
